import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { BsWhatsapp } from "react-icons/bs";
import {
  FiCalendar,
  FiInbox,
  FiLink2,
  FiPause,
  FiPhone,
  FiPlay,
  FiPlusCircle,
  FiSmartphone,
  FiBriefcase,
  FiX,
} from "react-icons/fi";
import { toast } from "react-toastify";
import {
  useCompleteAgentWhatsappOnboardingMutation,
  useDisconnectAgentWhatsappMutation,
  useToggleAgentWhatsappMutation,
} from "../../redux/api/agentWhatsappApi";
import { FB_LOGIN_CONFIG, formatWhatsappDate } from "./data";
import { useTokens } from "./tokens";

const InfoTile = ({ icon, label, value }) => {
  const tokens = useTokens();
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${tokens.border}`, bgcolor: tokens.surface }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "grid",
          placeItems: "center",
          bgcolor: tokens.waLight,
          color: tokens.wa,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", color: tokens.inkFaint, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        <Typography variant="body1" noWrap sx={{ fontWeight: 700, color: tokens.ink }}>
          {value || "-"}
        </Typography>
      </Box>
    </Stack>
  );
};

// Popup: choose which Embedded Signup flow to start.
export const ConnectWhatsAppDialog = ({ open, onClose, onSelect }) => {
  const tokens = useTokens();
  const options = [
    {
      existing: true,
      icon: <FiSmartphone size={22} />,
      title: "Use my existing WhatsApp",
      text: "Keep using your WhatsApp Business app number. Chats stay on your phone and in the CRM.",
    },
    {
      existing: false,
      icon: <FiPlusCircle size={22} />,
      title: "Register a new number",
      text: "Set up a fresh number (or WhatsApp Business account) dedicated to the CRM.",
    },
  ];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, bgcolor: tokens.surface, backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: tokens.ink, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Connect WhatsApp
        <IconButton size="small" onClick={onClose}>
          <FiX />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, mb: 2 }}>
          Choose how you want to connect. A Meta window will open to finish setup.
        </Typography>
        <Stack spacing={1.5}>
          {options.map((o) => (
            <Box
              key={o.title}
              onClick={() => onSelect(o.existing)}
              sx={{
                display: "flex",
                gap: 2,
                p: 2,
                borderRadius: 2.5,
                cursor: "pointer",
                border: `1px solid ${tokens.border}`,
                transition: "all .15s ease",
                "&:hover": { borderColor: tokens.wa, bgcolor: tokens.waLight },
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: tokens.wa,
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                {o.icon}
              </Box>
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
                  {o.title}
                </Typography>
                <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                  {o.text}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// Runs Meta Embedded Signup and hands the result to the backend.
export const useAgentWhatsappConnect = () => {
  const [completeOnboarding, { isLoading: isConnecting }] =
    useCompleteAgentWhatsappOnboardingMutation();
  const signupInfo = useRef({});
  const existingRef = useRef(false);

  // Meta posts the WABA / phone number ids while the popup runs; the auth code
  // comes from the FB.login callback. Keep the ids for the backend call.
  useEffect(() => {
    const onMessage = (event) => {
      if (!/^https:\/\/([\w-]+\.)*facebook\.com$/.test(event.origin || "")) return;
      try {
        const payload = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (payload?.type === "WA_EMBEDDED_SIGNUP" && payload.event !== "CANCEL") {
          signupInfo.current = {
            waba_id: payload.data?.waba_id,
            phone_number_id: payload.data?.phone_number_id,
          };
        }
      } catch (e) {
        // not a JSON message
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const connect = (existingNumber = false) => {
    if (!window.FB) {
      toast.error("Facebook SDK is still loading. Please try again in a moment.");
      return;
    }
    signupInfo.current = {};
    existingRef.current = !!existingNumber;

    window.FB.login(
      (response) => {
        const code = response?.authResponse?.code;
        if (!code) {
          toast.info("WhatsApp setup was cancelled.");
          return;
        }
        // The signup message can land just after the login callback.
        setTimeout(() => {
          completeOnboarding({
            code,
            waba_id: signupInfo.current.waba_id ?? null,
            phone_number_id: signupInfo.current.phone_number_id ?? null,
            existing_number: existingRef.current,
          })
            .unwrap()
            .then(() => toast.success("WhatsApp connected!"))
            .catch((error) => {
              toast.error(
                error?.data?.error || error?.data?.message || "Connection failed. Please try again."
              );
            });
        }, 800);
      },
      FB_LOGIN_CONFIG(existingNumber)
    );
  };

  return { connect, isConnecting };
};

// Overview tab body.
const AgentWhatsappCard = ({ status, isLoading, onOpenInbox, onConnect, isConnecting }) => {
  const tokens = useTokens();
  const [disconnectWhatsapp, { isLoading: isDisconnecting }] = useDisconnectAgentWhatsappMutation();
  const [toggleWhatsapp, { isLoading: isToggling }] = useToggleAgentWhatsappMutation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleDisconnect = async () => {
    setConfirmOpen(false);
    try {
      await disconnectWhatsapp().unwrap();
    } catch (error) {
      // toast is raised by the mutation
    }
  };

  const handleToggle = async () => {
    try {
      await toggleWhatsapp().unwrap();
    } catch (error) {
      // toast is raised by the mutation
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={28} thickness={4} sx={{ color: tokens.wa }} />
      </Box>
    );
  }

  if (!status?.connected) {
    return (
      <Stack alignItems="center" textAlign="center" sx={{ py: 5, px: 2 }}>
        <Box
          sx={{ width: 56, height: 56, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: tokens.softFaint, mb: 2 }}
        >
          <BsWhatsapp size={24} color={tokens.inkFaint} />
        </Box>
        <Typography fontWeight={700} sx={{ color: tokens.ink }}>
          WhatsApp not connected yet
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.inkMuted, maxWidth: 380, mt: 0.5, mb: 3 }}>
          Connect your business WhatsApp to chat with leads and reply automatically, all from the CRM.
        </Typography>
        <Button
          variant="contained"
          onClick={onConnect}
          disabled={isConnecting}
          startIcon={isConnecting ? <CircularProgress size={16} thickness={5} sx={{ color: "#fff" }} /> : <FiLink2 size={16} />}
          sx={{ bgcolor: tokens.wa, color: "#fff", "&:hover": { bgcolor: tokens.waDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
        >
          {isConnecting ? "Connecting..." : "Connect WhatsApp"}
        </Button>
      </Stack>
    );
  }

  const enabled = !!status.isEnabled;

  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <InfoTile icon={<FiPhone size={18} />} label="WhatsApp" value={status.displayPhoneNumber} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InfoTile icon={<FiBriefcase size={18} />} label="Business" value={status.businessName} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InfoTile icon={<FiCalendar size={18} />} label="Connected" value={formatWhatsappDate(status.connectedAt)} />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          p: 2,
          borderRadius: 2.5,
          bgcolor: enabled ? tokens.waLight : tokens.warningBg,
          border: `1px solid ${enabled ? "rgba(37,211,102,0.25)" : "rgba(245,158,11,0.25)"}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: enabled ? tokens.wa : tokens.warning }} />
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
              Automation:{" "}
              <span style={{ color: enabled ? tokens.wa : tokens.warning }}>{enabled ? "Active" : "Paused"}</span>
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
              {enabled ? "WhatsApp replies are sent automatically." : "Replies are not being sent automatically."}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          onClick={handleToggle}
          disabled={isToggling}
          startIcon={isToggling ? <CircularProgress size={14} thickness={5} /> : enabled ? <FiPause size={14} /> : <FiPlay size={14} />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, color: enabled ? tokens.inkMuted : tokens.wa, borderColor: enabled ? tokens.border : tokens.wa }}
        >
          {enabled ? "Pause" : "Resume"}
        </Button>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<FiInbox size={16} />}
          onClick={onOpenInbox}
          sx={{ bgcolor: tokens.wa, color: "#fff", "&:hover": { bgcolor: tokens.waDark }, textTransform: "none", fontWeight: 700, borderRadius: 2, boxShadow: "none" }}
        >
          Open Inbox
        </Button>
      </Box>

      <Divider sx={{ my: 3, borderColor: tokens.border }} />

      <Box
        sx={{
          p: 2,
          borderRadius: 2.5,
          border: `1px solid ${tokens.danger}40`,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ color: tokens.ink }}>
            Disconnect WhatsApp
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
            Stops automated replies and removes inbox access until you reconnect.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => setConfirmOpen(true)}
          disabled={isDisconnecting}
          startIcon={<FiX size={14} />}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
        >
          Disconnect
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Disconnect WhatsApp?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your WhatsApp number will be disconnected from the CRM. You can connect it again at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentWhatsappCard;
